import React from "react";
import Navbar from "../Navbar";
import {
  Box,
  Button,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Divider,
  Paper,
  InputAdornment,
} from "@mui/material";
import { TabPanel } from "../../../components/Tabs/TabPanel";
import {
  CountryCodeSelect,
  CustomTextField,
} from "../../../components/formsComponents";
import { baseAxios } from "../../../api/api";
import { CustomAlert } from "../../../components/formsComponents/CustomAlert";
import { useFormik } from "formik";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff,
  BusinessOutlined as BanquetIcon,
  LocationOn as LocationIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../contexts/AuthProvider/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useDispatchWrapper } from "../../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../../redux/messages/messagesActions";
import { ErrorMessage } from "../../../components/ErrorMessage/ErrorMessage";
import { ILocationResponsePayload } from "../../../components/LocationAutoComplete/LocationAutoComplete";
import { LocationAutoCompleteWithManualAddress } from "../../../components/LocationAutoComplete/LocationAutoCompleteWithManualAddress";
import { uploadOnboardingFile } from "../../../components/FileUpload/utils";
import { ImageUpload } from "../../../components/ImageUpload/ImageUpload";
import { formatCompanyAddress } from "../../../helpers/formatCompanyAddress";
import { MuiNumberField } from "../../../components/formsComponents/MuiNumberField";
import { REACT_APP_NAV_ROUTES } from "../../../router/AppNavigation";
import { config } from "../../../config";
import { CountryCodeSelector, CountrySelector } from "../../../components/CountrySelector";
import { COUNTRIES } from "../../../constants/countries";

const steps = ["Mobile Verification", "Company", "User Profile"];

export const TalkvitUserSignUp = () => {
  const theme = useTheme();
  const dispatch = useDispatchWrapper();

  const [activeStep, setActiveStep] = React.useState(0);
  const [showOTP, setShowOTP] = React.useState(false);
  const { saveSignupData } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const [resendTimer, setResendTimer] = React.useState(0);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [mobileData, setMobileData] = React.useState<{
    mobileNumber: string;
    otp: string;
    onboarding_uuid: string;
    token: string;
    loading: boolean;
  }>({
    mobileNumber: "",
    otp: "",
    onboarding_uuid: "",
    token: "",
    loading: false,
  });
  const [loading, setLoading] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const [selectedCountryIso, setSelectedCountryIso] = React.useState("IN");
  const selectedCountryCode = COUNTRIES.find(c => c.code === selectedCountryIso)?.dialCode || "+91";
  const [mobileExists, setMobileExists] = React.useState(false);

  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [mobileError, setMobileError] = React.useState<string | null>(null);
  const [otpError, setOtpError] = React.useState<string | null>(null);

  const [showError, setShowError] = React.useState<string[] | null>(null);
  const [step3Submitted, setStep3Submitted] = React.useState(false);

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    validateForm,
  } = useFormik({
    initialValues: {
      company_outlet_name: "",
      mobile: "",
      email: "",
      logo: "",
      user_password: "",
      first_name: "",
      last_name: "",
      status: "ACTIVE",
      confirm_password: "",
      company_outlet_address_line1: "",
      company_outlet_address_line2: "",
      company_outlet_address_city: "",
      company_outlet_address_state_or_province: "",
      company_outlet_address_country: "",
      company_outlet_address_postal_code: "",
      company_outlet_region: "",
    },

    validate: (values) => {
      const errors: any = {};
      // Step 0: Mobile Verification handled outside Formik

      // Step 1: Company Details
      if (activeStep === 1) {
        if (!values.company_outlet_name) {
          errors.company_outlet_name = "Company name is required.";
        }
      }
      // Step 3: User Profile
      if (activeStep === 2) {
        if (!values.first_name) {
          errors.first_name = "First name is required.";
        }
        // if (!values.last_name) {
        //   errors.last_name = "Last name is required.";
        // }
        if (!values.email) {
          errors.email = "Email is required.";
        }
        if (
          !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(values.email)
        ) {
          errors.email = "Enter a valid email address.";
        }
        if (!values.user_password) {
          errors.user_password = "Password is required.";
        } else if (values.user_password.length < 6) {
          errors.user_password = "Password must be at least 6 characters.";
        }
        if (!values.confirm_password) {
          errors.confirm_password = "Confirm password is required.";
        } else if (values.user_password !== values.confirm_password) {
          errors.confirm_password = "Passwords do not match.";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      setError(null);
      if (activeStep === 0) {
        return null;
      }
      if (activeStep === 1) {
        handleNext();
        return;
      }

      try {
        setLoading(true);
        dispatch(saveLoaderProgress());

        const asPayload = {
          company_outlet_name: values.company_outlet_name,
        };

        const logoPath = await uploadOnboardingFile({
          file: file,
          module_name: "COMPANY_OUTLET_LOGO",
          defaultReturn: values.logo || "",
          asPayload: asPayload,
          headers: { "onboarding-token": mobileData.token },
          onWarning: (message) => {
            dispatch(
              showMessage({
                type: "warning",
                message: message,
                displayAs: "snackbar",
              }),
            );
          },
        });

        const payload = {
          company_outlet_name: values.company_outlet_name,
          mobile: mobileData.mobileNumber,
          email: values.email,
          user_password: values.user_password,
          first_name: values.first_name,
          last_name: values.last_name || "",
          status: values.status,
          logo: logoPath,
          company_outlet_address_line1: values.company_outlet_address_line1,
          company_outlet_address_line2: values.company_outlet_address_line2,
          company_outlet_address_city: values.company_outlet_address_city,
          company_outlet_address_state_or_province:
            values.company_outlet_address_state_or_province,
          company_outlet_address_country: values.company_outlet_address_country,
          company_outlet_address_postal_code:
            values.company_outlet_address_postal_code,
          company_outlet_region: values.company_outlet_region,
        };

        const response = await baseAxios.post(`/user/signup-user`, payload, {
          headers: {
            "onboarding-token": mobileData.token,
          },
        });

        const data = response.data.data.data;

        // Save to localStorage using the AuthProvider
        if (data) {
          saveSignupData(data, () => {
            navigate(REACT_APP_NAV_ROUTES.subscription.pricing);
          });
        }
      } catch (error: any) {
        setError(
          error.response?.data?.message || "An unexpected error occurred",
        );
      } finally {
        dispatch(saveLoaderCompleted());
        setLoading(false);
      }
    },
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep3Submit = async () => {
    setStep3Submitted(true);
    const formErrors = await validateForm();
    const step3Fields = [
      "first_name",
      "last_name",
      "email",
      "user_password",
      "confirm_password",
    ];
    const step3Errors = Object.keys(formErrors).filter((field) =>
      step3Fields.includes(field),
    );

    if (step3Errors.length === 0) {
      // Clear any previous error and submit
      setError(null);
      handleSubmit();
    } else {
      setError("Please fill in all required fields correctly.");
    }
  };

  const handleGenerateOTP = async () => {
    setMobileError(null);
    setOtpError(null);
    setMobileExists(false);
    const fullMobileNumber = selectedCountryCode + mobileData.mobileNumber;

    // Mobile validation - now validating the full number with country code
    if (!mobileData.mobileNumber) {
      setMobileError("Mobile number is required.");
      return;
    } else if (!/^[0-9]+$/.test(mobileData.mobileNumber)) {
      setMobileError("Mobile number must contain only digits.");
      return;
    } else if (mobileData.mobileNumber.length !== 10) {
      setMobileError("Mobile number must be exactly 10 digits.");
      return;
    }

    try {
      setError(null);
      setMobileData((prev) => ({ ...prev, loading: true }));
      const response = await baseAxios.post(
        `/authentication/generate-otp-mobile`,
        {
          mobile: fullMobileNumber,
        },
      );

      const data = response.data.data;

      // Check if response indicates user already exists (some APIs return this in success response)
      if (
        data?.user_exists ||
        data?.exists ||
        response.data?.user_exists ||
        response.data?.exists
      ) {
        setMobileExists(true);
        setShowOTP(false);
        setMobileData((prev) => ({
          ...prev,
          loading: false,
          otp: "",
          onboarding_uuid: "",
          token: "",
        }));
        setError(
          "Mobile number already exists. Please try a different number.",
        );
        return;
      }

      setMobileData((prev) => ({
        ...prev,
        onboarding_uuid: data.onboarding_uuid,
        loading: false,
        mobileNumber: fullMobileNumber,
      }));
      setShowOTP(true);
      setResendTimer(60); // Start 60 second countdown
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.data?.message ||
        "";
      const errorString = errorMessage.toLowerCase();

      if (
        error.response?.status === 401 ||
        errorString.includes("mobile already exists") ||
        errorString.includes("already exists") ||
        errorString.includes("mobile number already") ||
        errorString.includes("user already exists") ||
        errorString.includes("mobile exists") ||
        errorString.includes("phone number already") ||
        errorString.includes("number already registered") ||
        errorString.includes("reset the password") ||
        errorString.includes("you can login") ||
        errorString.includes("either you reset") ||
        errorString.includes("login it") ||
        error.response?.status === 409 || // Conflict status often means resource exists
        (error.response?.status === 400 && errorString.includes("exists"))
      ) {
        setMobileExists(true);
        setShowOTP(false); // Reset OTP view if mobile exists
        setMobileData((prev) => ({
          ...prev,
          otp: "",
          onboarding_uuid: "",
          token: "",
        }));
        // Keep the original error message from API if it's informative
        if (
          errorMessage &&
          (errorString.includes("reset") || errorString.includes("login"))
        ) {
          setError(errorMessage);
        } else {
          setError(
            "Mobile number already exists. Please try a different number.",
          );
        }
      } else {
        setError(
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.data?.message ||
          "An unexpected error occurred",
        );
      }
    } finally {
      setMobileData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleRecoverPassword = () => {
    navigate("/recover-password", {
      state: {
        mobile: mobileData.mobileNumber,
      },
    });
  };
  const handleLogin = () => {
    navigate("/login", {
      state: {
        mobile: mobileData.mobileNumber,
      },
    });
  };

  const handleResendOTP = async () => {
    setOtpError(null);
    setError(null);
    setSuccessMessage(null);

    try {
      setMobileData((prev) => ({ ...prev, loading: true }));
      const response = await baseAxios.post(
        `/authentication/generate-otp-mobile`,
        {
          mobile: mobileData.mobileNumber,
        },
      );
      const data = response.data.data;
      setMobileData((prev) => ({
        ...prev,
        onboarding_uuid: data.onboarding_uuid,
        loading: false,
        otp: "", // Clear previous OTP
      }));
      setResendTimer(60); // Start 60 second countdown
      setSuccessMessage("OTP resent successfully!");
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || "An unexpected error occurred");
    } finally {
      setMobileData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleVerifyOTP = async () => {
    setOtpError(null);
    if (!mobileData.otp) {
      setOtpError("OTP is required.");
      return;
    } else if (!/^[0-9]{4,6}$/.test(mobileData.otp)) {
      setOtpError("Enter a valid OTP.");
      return;
    }
    try {
      setError(null);
      setMobileData((prev) => ({ ...prev, loading: true }));
      const response = await baseAxios.post(
        `/authentication/validate-otp-get-token-mobile`,
        {
          mobile: mobileData.mobileNumber,
          otp: mobileData.otp,
          onboarding_uuid: mobileData.onboarding_uuid,
        },
      );
      const data = response.data.data;
      setMobileData((prev) => ({
        ...prev,
        token: data.onboarding_token,
        loading: false,
      }));
      setShowOTP(true);
      handleNext();
    } catch (error: any) {
      setError(error.response?.data?.message || "An unexpected error occurred");
    } finally {
      setMobileData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCompanyAddressChange = (data: ILocationResponsePayload) => {
    setFieldValue("company_outlet_address_line1", data.address_line1 || "");
    setFieldValue("company_outlet_address_line2", data.address_line2 || "");
    setFieldValue("company_outlet_address_city", data.city || "");
    setFieldValue("company_outlet_address_state_or_province", data.state || "");
    setFieldValue("company_outlet_address_country", data.country || "");
    setFieldValue("company_outlet_address_postal_code", data.postal_code || "");
    setFieldValue("company_outlet_region", data.region || "");
  };

  // Countdown timer effect
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  function setCountryCode(code: string) {
    throw new Error("Function not implemented.");
  }

  return (
    <>
      <Navbar pageType="SIGNUP" />
      <Stack mt={15}>
        <Typography
          variant="h1"
          color="primary.main"
          fontSize={"3rem"}
          fontWeight={600}
          textAlign={"center"}
        >
          Welcome Aboard
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.grey[600],
            fontSize: "1.3rem",
            textAlign: "center",
            mb: 5,
          }}
        >
          Let's set up your account in a few simple steps
        </Typography>

        <Stack mt={3}>
          <Grid container>
            <Grid item xs={12} md={3}></Grid>
            <Grid item xs={12} md={6}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={12} md={3}></Grid>
          </Grid>
        </Stack>

        <Grid container mt={5}>
          <Grid item xs={12} md={3.5}></Grid>
          <Grid item xs={12} md={5}>
            {error && (
              <CustomAlert
                severity="error"
                sx={{ mb: 2, borderRadius: "10px" }}
              >
                {error}
              </CustomAlert>
            )}

            {successMessage && (
              <CustomAlert
                severity="success"
                sx={{ mb: 2, borderRadius: "10px" }}
              >
                {successMessage}
              </CustomAlert>
            )}

            <form onSubmit={handleSubmit} autoComplete="off">
              <TabPanel index={0} value={activeStep}>
                <Stack
                  sx={{
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: "20px",
                    backgroundColor: "#fff",
                    p: 4,
                    height: "100%",
                  }}
                >
                  <Typography variant="h3" fontWeight={600} fontSize={"1.8rem"}>
                    Mobile Verification
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.grey[600],
                      fontSize: "1.2rem",
                      mt: 1,
                      mb: 3,
                    }}
                  >
                    Verify your mobile number
                  </Typography>

                  {showOTP && (
                    <Stack
                      p={3}
                      sx={{
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: "20px",
                        backgroundColor: theme.palette.grey[50],
                      }}
                    >
                      <Typography
                        variant="h3"
                        fontWeight={600}
                        fontSize={"1.4rem"}
                      >
                        OTP sent to
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        fontSize={"1rem"}
                        mt={1}
                      >
                        {mobileData.mobileNumber}
                      </Typography>
                    </Stack>
                  )}

                  {!showOTP ? (
                    <Stack>
                      <Typography
                        variant="body1"
                        fontSize={"1.2rem"}
                        fontWeight={600}
                      >
                        Mobile Number
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4} md={3}>
                          <CountryCodeSelector
                            sx={{
                              minHeight: 50,
                              mt: 1,
                            }}
                            value={selectedCountryIso}
                            onChange={(country) => {
                              setSelectedCountryIso(country.code);
                            }}
                          />
                        </Grid>
                        <Grid item xs={8} md={9}>
                          <CustomTextField
                            sx={{
                              mt: 1,
                              "& .MuiInputBase-root": {
                                minHeight: 50,
                              },
                            }}
                            type="number"
                            placeholder="Enter your mobile number"
                            fullWidth
                            value={mobileData.mobileNumber.replace(
                              selectedCountryCode,
                              "",
                            )}
                            onKeyDown={(e) => {
                              if (["e", "E", "+", "-", "."].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              const digitsOnly = value.replace(/\D/g, "");
                              if (digitsOnly.length <= 10) {
                                setMobileData((prev) => ({
                                  ...prev,
                                  mobileNumber: digitsOnly,
                                }));
                                if (mobileExists) {
                                  setMobileExists(false);
                                  setError(null);
                                }
                              }
                            }}
                            inputProps={{
                              maxLength: 10,
                            }}
                            error={!!mobileError}
                            helperText={mobileError || undefined}
                          />
                        </Grid>
                      </Grid>
                      {mobileExists && (
                        <Stack spacing={2} mt={3}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: theme.palette.grey[600],
                              fontSize: "1rem",
                              textAlign: "center",
                              mb: 1,
                            }}
                          >
                            Choose an option to continue:
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            sx={{
                              py: 1.5,
                              fontSize: "1.1rem",
                              fontWeight: 600,
                            }}
                            onClick={handleLogin}
                          >
                            Login to Your Account
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            fullWidth
                            sx={{
                              py: 1.5,
                              fontSize: "1.1rem",
                              fontWeight: 600,
                            }}
                            onClick={handleRecoverPassword}
                          >
                            Recover Your Password
                          </Button>
                        </Stack>
                      )}
                      {!mobileExists && (
                        <Stack mt={3}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{
                              px: 5,
                              py: 1.5,
                              fontSize: "1.2rem",
                              alignSelf: "center",
                              mt: 3,
                            }}
                            disabled={
                              Boolean(errors.mobile) || mobileData.loading
                            }
                            type="button"
                            onClick={handleGenerateOTP}
                          >
                            {mobileData.loading ? "Please wait..." : "Send OTP"}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    <Stack spacing={3}>
                      <Typography
                        variant="body1"
                        fontSize={"1.2rem"}
                        fontWeight={600}
                        mt={2}
                      >
                        OTP
                      </Typography>
                      <CustomTextField
                        sx={{
                          mt: 1,
                          "& .MuiInputBase-root": {
                            minHeight: 50,
                          },
                        }}
                        placeholder="OTP"
                        fullWidth
                        value={mobileData.otp}
                        onChange={(e) => {
                          const trimmedValue = e.target.value.trimStart();
                          setMobileData((prev) => ({
                            ...prev,
                            otp: trimmedValue,
                          }));
                        }}
                        error={!!otpError}
                        helperText={otpError}
                      />
                      <Stack spacing={3} mt={4}>
                        {/* Resend OTP Section */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.grey[200]}`,
                          }}
                        >
                          {resendTimer > 0 ? (
                            <>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.9rem",
                                    color: theme.palette.grey[600],
                                  }}
                                >
                                  Didn't receive OTP?
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    color: theme.palette.grey[800],
                                    mt: 0.5,
                                  }}
                                >
                                  Resend in {resendTimer}s
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.95rem",
                                  color: theme.palette.grey[700],
                                }}
                              >
                                Didn't receive OTP?
                              </Typography>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={handleResendOTP}
                                disabled={mobileData.loading}
                                sx={{
                                  textTransform: "none",
                                  fontSize: "1rem",
                                  fontWeight: 600,
                                  px: 3,
                                  py: 1,
                                }}
                              >
                                {mobileData.loading
                                  ? "Sending..."
                                  : "Resend OTP"}
                              </Button>
                            </>
                          )}
                        </Box>

                        {/* Verify Button */}
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          fullWidth
                          sx={{
                            py: 1.8,
                            fontSize: "1.2rem",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "10px",
                          }}
                          onClick={handleVerifyOTP}
                          disabled={mobileData.loading}
                        >
                          {mobileData.loading
                            ? "Verifying..."
                            : "Verify & Continue"}
                        </Button>

                        {/* Change Mobile Number Link */}
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontSize: "1rem",
                              color: theme.palette.grey[600],
                            }}
                          >
                            Wrong number?{" "}
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            onClick={() => {
                              setShowOTP(false);
                              setResendTimer(0);
                              setMobileData((prev) => ({
                                ...prev,
                                mobileNumber: prev.mobileNumber.replace(
                                  selectedCountryCode,
                                  "",
                                ),
                                otp: "",
                              }));
                            }}
                            sx={{
                              fontSize: "1rem",
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: theme.palette.primary.dark,
                              },
                            }}
                          >
                            Change mobile number
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </TabPanel>
              <TabPanel index={1} value={activeStep}>
                <Stack
                  sx={{
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: "16px",
                    backgroundColor: "#fff",
                    p: { xs: 3, md: 4 },
                    height: "100%",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  {/* Header Section */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.grey[100],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <BanquetIcon
                        sx={{ fontSize: 28, color: theme.palette.grey[600] }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="h3"
                        fontWeight={700}
                        fontSize={"1.6rem"}
                        color="text.primary"
                      >
                        Company Details
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.grey[500],
                          fontSize: "1rem",
                          mt: 0.5,
                        }}
                      >
                        Tell us about your business
                      </Typography>
                    </Box>
                  </Box>

                  <Stack spacing={1} mb={3}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        variant="body1"
                        fontSize={"0.95rem"}
                        fontWeight={600}
                        color="text.primary"
                      >
                        Company Name
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: theme.palette.error.main,
                          fontWeight: 600,
                        }}
                      >
                        *
                      </Typography>
                    </Box>
                    <CustomTextField
                      sx={{
                        "& .MuiInputBase-root": {
                          minHeight: 48,
                          borderRadius: "10px",
                        },
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: theme.palette.grey[400],
                          },
                        },
                      }}
                      placeholder="e.g., Horizon Connect Ltd."
                      fullWidth
                      name="company_outlet_name"
                      value={values.company_outlet_name}
                      onChange={handleChange}
                      error={!!errors.company_outlet_name}
                      helperText={errors.company_outlet_name}
                    />
                  </Stack>

                  {/* Location Section */}
                  <Stack spacing={1} mb={3}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: isDesktop ? "center" : "flex-start",
                        gap: 1,
                        flexDirection: isDesktop ? "row" : "column",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <LocationIcon
                          sx={{ fontSize: 20, color: theme.palette.grey[600] }}
                        />
                        <Typography
                          variant="body1"
                          fontSize={"0.95rem"}
                          fontWeight={600}
                          color="text.primary"
                        >
                          Location
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.grey[500],
                          fontSize: "0.85rem",
                        }}
                      >
                        (Search or enter manually)
                      </Typography>
                    </Box>
                    <LocationAutoCompleteWithManualAddress
                      id={`company-address`}
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      value={formatCompanyAddress({
                        line1: values.company_outlet_address_line1,
                        line2: values.company_outlet_address_line2,
                        city: values.company_outlet_address_city,
                        state: values.company_outlet_address_state_or_province,
                        region: values.company_outlet_region,
                        country: values.company_outlet_address_country,
                        postalCode: values.company_outlet_address_postal_code,
                      })}
                      onLocationChange={(data) =>
                        handleCompanyAddressChange(data)
                      }
                    />
                    {errors.company_outlet_address_line1 && (
                      <ErrorMessage
                        value={errors.company_outlet_address_line1 || ""}
                      />
                    )}
                  </Stack>

                  {/* Address Details Card */}
                  <Box
                    sx={{
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: "14px",
                      p: 3,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: theme.palette.grey[600],
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        letterSpacing: "0.08em",
                        mb: 2,
                        display: "block",
                      }}
                    >
                      ADDRESS DETAILS
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <CustomTextField
                          label="Address Line 1"
                          placeholder="Street address, building name"
                          fullWidth
                          name="company_outlet_address_line1"
                          value={values.company_outlet_address_line1}
                          onChange={handleChange}
                          error={!!errors.company_outlet_address_line1}
                          helperText={errors.company_outlet_address_line1}
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CustomTextField
                          label="Address Line 2"
                          placeholder="Area, landmark"
                          fullWidth
                          name="company_outlet_address_line2"
                          value={values.company_outlet_address_line2}
                          onChange={handleChange}
                          error={!!errors.company_outlet_address_line2}
                          helperText={errors.company_outlet_address_line2}
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CustomTextField
                          label="City"
                          placeholder="Enter city"
                          fullWidth
                          name="company_outlet_address_city"
                          value={values.company_outlet_address_city}
                          onChange={handleChange}
                          error={!!errors.company_outlet_address_city}
                          helperText={errors.company_outlet_address_city}
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CustomTextField
                          label="State/Province"
                          placeholder="Enter state or province"
                          fullWidth
                          name="company_outlet_address_state_or_province"
                          value={
                            values.company_outlet_address_state_or_province
                          }
                          onChange={handleChange}
                          error={
                            !!errors.company_outlet_address_state_or_province
                          }
                          helperText={
                            errors.company_outlet_address_state_or_province
                          }
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CustomTextField
                          label="Country"
                          placeholder="Enter country"
                          fullWidth
                          name="company_outlet_address_country"
                          value={values.company_outlet_address_country}
                          onChange={handleChange}
                          error={!!errors.company_outlet_address_country}
                          helperText={errors.company_outlet_address_country}
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CustomTextField
                          label="Region"
                          placeholder="Enter region"
                          fullWidth
                          name="company_outlet_region"
                          value={values.company_outlet_region}
                          onChange={handleChange}
                          error={!!errors.company_outlet_region}
                          helperText={errors.company_outlet_region}
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CustomTextField
                          label="Postal Code"
                          placeholder="Enter postal code"
                          fullWidth
                          name="company_outlet_address_postal_code"
                          value={values.company_outlet_address_postal_code}
                          onChange={handleChange}
                          error={!!errors.company_outlet_address_postal_code}
                          helperText={errors.company_outlet_address_postal_code}
                          sx={{
                            "& .MuiInputBase-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Logo Upload Section */}
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CloudUploadIcon
                        sx={{ fontSize: 20, color: theme.palette.grey[600] }}
                      />
                      <Typography
                        variant="body1"
                        fontSize={"0.95rem"}
                        fontWeight={600}
                        color="text.primary"
                      >
                        Company Logo
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.grey[500],
                          fontSize: "0.85rem",
                          fontStyle: "italic",
                        }}
                      >
                        (optional)
                      </Typography>
                    </Box>

                    <ImageUpload
                      label="Logo"
                      value={values.logo}
                      onChange={(file: File | null) => {
                        setFile(file);
                      }}
                      onDelete={() => {
                        setFile(null);
                      }}
                      placeholder="Upload Company Logo"
                      s3BaseUrl={`${config.assets.s3BaseUrl}/`}
                    />

                    {errors.logo && <ErrorMessage value={errors.logo} />}
                  </Stack>

                  {/* Continue Button */}
                  <Stack
                    mt={5}
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      sx={{
                        px: 6,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        borderRadius: "10px",
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
                        },
                      }}
                    >
                      Continue
                    </Button>
                  </Stack>
                </Stack>
              </TabPanel>

              <TabPanel index={2} value={activeStep}>
                <Stack
                  sx={{
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: "20px",
                    backgroundColor: "#fff",
                    p: 4,
                    height: "100%",
                  }}
                >
                  <Typography variant="h3" fontWeight={600} fontSize={"1.8rem"}>
                    User Profile
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.grey[600],
                      fontSize: "1.2rem",
                      mt: 1,
                      mb: 3,
                    }}
                  >
                    Complete your profile
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack>
                        <Typography
                          variant="body1"
                          fontSize={"1.2rem"}
                          fontWeight={600}
                        >
                          First Name
                        </Typography>
                        <CustomTextField
                          sx={{
                            mt: 1,
                            "& .MuiInputBase-root": {
                              minHeight: 50,
                            },
                          }}
                          placeholder="Enter your first name"
                          fullWidth
                          name="first_name"
                          value={values.first_name}
                          onChange={handleChange}
                          error={step3Submitted && !!errors.first_name}
                          helperText={step3Submitted ? errors.first_name : ""}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack>
                        <Typography
                          variant="body1"
                          fontSize={"1.2rem"}
                          fontWeight={600}
                        >
                          Last Name
                        </Typography>
                        <CustomTextField
                          sx={{
                            mt: 1,
                            "& .MuiInputBase-root": {
                              minHeight: 50,
                            },
                          }}
                          placeholder="Enter your last name"
                          fullWidth
                          name="last_name"
                          value={values.last_name}
                          onChange={handleChange}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={12}>
                      <Stack>
                        <Typography
                          variant="body1"
                          fontSize={"1.2rem"}
                          fontWeight={600}
                        >
                          Email
                        </Typography>
                        <CustomTextField
                          sx={{
                            mt: 1,
                            "& .MuiInputBase-root": {
                              minHeight: 50,
                            },
                          }}
                          placeholder="Enter your email"
                          fullWidth
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          error={step3Submitted && !!errors.email}
                          helperText={step3Submitted ? errors.email : ""}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack>
                        <Typography
                          variant="body1"
                          fontSize={"1.2rem"}
                          fontWeight={600}
                        >
                          Password
                        </Typography>
                        <CustomTextField
                          sx={{
                            mt: 1,
                            "& .MuiInputBase-root": {
                              minHeight: 50,
                            },
                          }}
                          type={showPassword ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          placeholder="Enter your password"
                          fullWidth
                          name="user_password"
                          value={values.user_password}
                          onChange={handleChange}
                          error={step3Submitted && !!errors.user_password}
                          helperText={
                            step3Submitted ? errors.user_password : ""
                          }
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack>
                        <Typography
                          variant="body1"
                          fontSize={"1.2rem"}
                          fontWeight={600}
                        >
                          Confirm Password
                        </Typography>
                        <CustomTextField
                          sx={{
                            mt: 1,
                            "& .MuiInputBase-root": {
                              minHeight: 50,
                            },
                          }}
                          type={showPassword ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          placeholder="Enter your confirm password"
                          fullWidth
                          name="confirm_password"
                          value={values.confirm_password}
                          onChange={handleChange}
                          error={step3Submitted && !!errors.confirm_password}
                          helperText={
                            step3Submitted ? errors.confirm_password : ""
                          }
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  <Stack
                    mt={6}
                    direction={"row"}
                    alignItems={"center"}
                    spacing={4}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      fullWidth
                      sx={{
                        px: 5,
                        py: 1.5,
                        fontSize: "1.2rem",
                        alignSelf: "center",
                      }}
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        px: 5,
                        py: 1.5,
                        fontSize: "1.2rem",
                        alignSelf: "center",
                      }}
                      // type="submit"
                      onClick={handleStep3Submit}
                      disabled={loading}
                    >
                      {loading ? "Please wait..." : "Submit"}
                    </Button>
                  </Stack>
                </Stack>
              </TabPanel>
            </form>
          </Grid>
          <Grid item xs={12} md={3.5}></Grid>
        </Grid>
      </Stack>
    </>
  );
};
